import type { Plugin, App } from "vue";

import * as Sentry from "@sentry/vue";

import router from "@/router/router";
import { Env } from "@/utils/env-schema";

type TSentryEnv = "preproduction" | "production" | "development";

const sentryModeMapping: Partial<Record<typeof __MODE__, TSentryEnv>> = {
  preprod: "preproduction",
  production: "production",
};

function getEnv(): TSentryEnv {
  return sentryModeMapping[__MODE__] ?? "development";
}

function setupSentry(app: App, env: Env) {
  if (env.VITE_SENTRY_ENABLED !== "true") {
    return;
  }

  const environment = getEnv();

  Sentry.init({
    app,
    dsn: env.VITE_SENTRY_DSN,
    environment: environment,
    integrations: [
      Sentry.browserTracingIntegration({
        router: router,
      }),
    ],
    tracesSampleRate: env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    sendDefaultPii: env.VITE_SENTRY_SEND_DEFAULT_PII,
    release: `${__APP_VERSION__}`,
  });
}

function setupConsoleLogs() {
  /**
   * Do not show any logs in production.
   * All errors will be caught by sentry, so in case of errors in production, please go to https://voltalis-eu.sentry.io/
   */
  if (__IS_PRODUCTION__) {
    console.log = (obj: unknown) => {
      Sentry.addBreadcrumb({
        category: "console",
        level: "log",
        message: JSON.stringify(obj),
      });
    };
    console.error = (obj: unknown) => {
      Sentry.addBreadcrumb({
        category: "console",
        level: "error",
        message: JSON.stringify(obj),
      });
    };
    console.warn = (obj: unknown) => {
      Sentry.addBreadcrumb({
        category: "console",
        level: "warning",
        message: JSON.stringify(obj),
      });
    };
    console.info = (obj: unknown) => {
      Sentry.addBreadcrumb({
        category: "console",
        level: "log",
        message: JSON.stringify(obj),
      });
    };
  }
}

export const enterpriseSentryPlugin: Plugin = {
  install(app, env) {
    setupSentry(app, env);
    setupConsoleLogs();
  },
};
